import Component from '@glimmer/component';
import EditorJS from '@editorjs/editorjs';
import type { OutputData } from '@editorjs/editorjs';
import { modifier } from 'ember-modifier';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { Button } from 'tio-ui/components/buttons';

interface SchoolNameHeaderData {
  schoolName: string;
}

interface LearnMoreLinkData {
  url: string;
}

interface FeaturedSchoolEditorArgs {
  onSave?: (data: OutputData) => void;
}

class SchoolNameHeader {
  static get toolbox() {
    return {
      title: 'School Name',
      icon: '<svg fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6"> <path stroke-linecap="round" stroke-linejoin="round" d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5" /></svg>',
    };
  }

  render() {
    const header = document.createElement('div');
    header.className = 'text-xl font-bold text-primary-500';
    header.id = 'school-name';
    header.contentEditable = 'true';
    header.innerText = 'Your School Name';
    return header;
  }

  save(): SchoolNameHeaderData {
    return {
      schoolName: document.getElementById('school-name')?.innerHTML || '',
    };
  }
}

class LearnMoreLink {
  wrapper?: HTMLDivElement;
  input?: HTMLInputElement;

  data;
  static get toolbox() {
    return {
      title: 'Learn More Link',
      icon: '<svg fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5H6A1.5 1.5 0 0 0 4.5 6v12A1.5 1.5 0 0 0 6 19.5h12A1.5 1.5 0 0 0 19.5 18v-7.5M17.25 6.75l-7.5 7.5M13.5 6.75h3.75V10.5"/></svg>',
    };
  }

  constructor({ data }: { data?: LearnMoreLinkData }) {
    this.data = data || { url: '' };
  }

  render() {
    this.wrapper = document.createElement('div');

    this.input = document.createElement('input');
    this.input.type = 'url';
    this.input.placeholder = 'Paste or type a URL...';
    this.input.value = this.data.url || '';
    this.input.className = 'border px-2 py-1 rounded w-full text-sm mb-2';

    const preview = document.createElement('a');
    preview.innerText = 'Learn More';
    preview.target = '_blank';
    preview.className = 'text-primary-500 underline hover:text-primary-700';

    if (this.data.url) {
      preview.href = this.data.url;
    } else {
      preview.style.display = 'none';
    }

    this.input.addEventListener('input', () => {
      const val = this.input?.value.trim() || '';
      if (val) {
        preview.href = val;
        preview.style.display = 'inline';
      } else {
        preview.style.display = 'none';
      }
    });

    this.input.addEventListener('blur', () => {
      const val = this.input?.value.trim();
      if (val) {
        if (this.input) {
          this.input.style.display = 'none';
        }
      }
    });

    preview.addEventListener('click', (e) => {
      e.preventDefault();
      if (this.input) {
        this.input.style.display = 'block';
      }
      this.input?.focus();
    });

    this.wrapper.appendChild(this.input);
    this.wrapper.appendChild(preview);

    return this.wrapper;
  }

  save(): LearnMoreLinkData {
    return {
      url: this.input?.value || '',
    };
  }
}

class FeaturedSchoolEditor extends Component<FeaturedSchoolEditorArgs> {
  editor!: EditorJS;

  @action
  save() {
    this.editor
      .save()
      .then((outputData) => {
        this.args.onSave?.(outputData);
      })
      .catch((error) => {
        console.log('Saving failed: ', error);
      });
  }

  // TODO: Return to this. Seems weird to tie the component to the modifier so explicitly.
  initializeEditor = modifier(() => {
    this.editor = new EditorJS({
      holder: 'editorjs',
      tools: {
        schoolName: SchoolNameHeader,
        learnMoreLink: LearnMoreLink,
      },
    });
  });
  <template>
    <div
      class="w-full border border-gray-200 rounded-lg bg-gray-50"
    >
      <div class="px-3 py-2 border-b border-gray-200">
        <h2 class="font-semibold text-lg">Add a featured school to your tuition assistance pages.</h2>
        <div class="p-4">
          <ol class="list-decimal">
            <li class="pb-2">Click the
              <b>+</b>
              symbol below and select "School Name", fill in the name of the school you are
              showcasing.</li>
            <li class="pb-2">Next click the
              <b>+</b>
              symbol again and select "text" to write a brief description of the school.
            </li>
            <li class="pb-2">Lastly click the
              <b>+</b>
              symbol and select "Learn More Link." Paste a link to the University program.
            </li>
          </ol>
        </div>
      </div>
      <div class="px-4 py-2 bg-white rounded-b-lg">
        <div id="editorjs" class="m-0" {{this.initializeEditor}}></div>
        <div class="w-full flex justify-center">
          <Button {{on "click" this.save}} @intent="primary" class="mx-1">
            Save
          </Button>
        </div>
      </div>
    </div>
  </template>
}

export { FeaturedSchoolEditor };
